import { add, endOfDay, format, setHours, setMinutes } from 'date-fns';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import ar from 'date-fns/locale/ar';
import { isEqual } from 'lodash';

const DATE_LOCALES: { [key: string]: Locale } = { es, fr, ar };
const DATE_FORMAT = 'dd/MM';
const DATE_TIME_FORMAT = 'dd/MM HH:mm';
const DATE_FORMAT_US = 'MM/dd';
const DATE_TIME_FORMAT_US = 'MM/dd h:mm a';
const MAX_LINE_CHARS = 27;
const MAX_SUBHEADING_LENGTH = 15;
const MAX_PIC_LENGTH = 10;

export function formatKDither16x16(
  orgPixels: number[],
  xSize: number,
  ySize: number
): number[] {
  let k = 0;
  let desPixels = [];

  for (let y = 0; y < ySize; ++y) {
    for (let x = 0; x < xSize; ++x) {
      if ((orgPixels[k] & 255) > 176) {
        desPixels[k] = 1;
      } else {
        desPixels[k] = 0;
      }

      ++k;
    }
  }

  return desPixels;
}

export function convertToWBMP(
  orgPixels: number[],
  xSize: number,
  ySize: number,
) {
  const x = orgPixels.filter((_, index) => index % 4 === 1);
  const y = formatKDither16x16(x, xSize, ySize);
  const results = Object.values(y).reduce((acc, item, index) => {
    if (index % 8 === 0) {
      acc = [...acc, [item]];
    } else {
      acc[acc.length - 1] = [...acc[acc.length - 1], item];
    }
    return acc;
  }, [] as number[][]).map((value: number[]) => parseInt(value.join(''), 2));

  return results;
}

export const isEOD = (calculation: string) => calculation === 'EOD'
export const isUBD = (calculation: string) => calculation === 'UBD'

const daysRegExp = /([+-]?[0-9]+)d(,\s*(EOD))?/i;
const hoursRegExp = /([+-]?[0-9]+)h/i;
const minsRegExp = /([+-]?[0-9]+)m/i;

export const processTransitionTime = (date: Date, calculation: string) => {
  if (isEOD(calculation)) {
    return {
      date: setMinutes(setHours(date, 23), 59)
    }
  }

  const daysRegExpOutput = daysRegExp.exec(calculation) || [];
  const hoursRegExpOutput = hoursRegExp.exec(calculation) || [];
  const minsRegExpOutput = minsRegExp.exec(calculation) || [];
  return {
    date: add(date, {
      days: parseInt(daysRegExpOutput[1] || '0', 10),
      hours: parseInt(hoursRegExpOutput[1] || '0', 10),
      minutes: parseInt(minsRegExpOutput[1] || '0', 10),
    }),
    isTimeEOD: Boolean(daysRegExpOutput[3] && isEOD(daysRegExpOutput[3]))
  }
}
interface ILabelHeadingSVGProps {
  productHeading: string,
  productSubheading: string
  pic: string
  picText: string
}
function LabelHeadingSVG({ productHeading, productSubheading, pic, picText }: ILabelHeadingSVGProps) {
  const productHeadingLength = productHeading.length;
  const hasSecondLine =  productHeadingLength > MAX_LINE_CHARS;
  const headingFontSize =
    productHeadingLength > MAX_LINE_CHARS ? 18
      : productHeadingLength > 20 ? 20
        : productHeadingLength > 14 ? 28
          : 30;
  const headingProps = {
    fontFamily: 'sans-serif',
    fontSize: headingFontSize,
    fontWeight: 'bold',
    fill: '#000000',
  };

  return (
    <>
      <text x="10" y={hasSecondLine ? 26 : 30} {...headingProps}>
        {productHeading.substring(0, MAX_LINE_CHARS)}
      </text>
      {hasSecondLine && (
        <text x="10" y="44" {...headingProps}>
          {productHeading.substring(MAX_LINE_CHARS, 50)}
        </text>
      )}

      {productSubheading && (
        <text x="10" y={hasSecondLine ? 64 : 60} {...headingProps} fontSize="20" fontStyle="italic" fontWeight="normal">
          {productSubheading.length > MAX_SUBHEADING_LENGTH ? productSubheading.substring(0,MAX_SUBHEADING_LENGTH) + '...' : productSubheading}
        </text>
      )}
      {
        pic && <>
        <rect fill="#fff" x="192" y={hasSecondLine ? 48 : 42} height="20" width="384"></rect>
        <text x="202" y={hasSecondLine ? 64 : 60} {...headingProps} fontSize="20" fontStyle="italic"
              fontWeight="normal">
          {picText} {pic.length > MAX_PIC_LENGTH ? pic.substring(0,MAX_PIC_LENGTH) + '...' : pic}
        </text>
      </>
      }
    </>
  )
}

export function createLabelSVG({
  brand,
                                 pic,
  productHeading,
  productSubheading,
  dateTime,
  calculationReady,
  calculationDiscard,
  dateDiscard,
  locale,
                                 picText,
  madeText,
  readyText,
  discardText
}: any) {
  const dateFormat = brand.id.includes('US') ? DATE_FORMAT_US : DATE_FORMAT;
  const dateTimeFormat = brand.id.includes('US') ? DATE_TIME_FORMAT_US : DATE_TIME_FORMAT;
  const dateFormatOptions = { locale: DATE_LOCALES[locale] };
  let readyDateTime = dateTime, readyDateTimeUI = format(readyDateTime, dateTimeFormat, dateFormatOptions);

  if (isEOD(calculationReady)) {
    readyDateTimeUI = calculationReady
  } else if (calculationReady) {
    const {
      date: readyDate,
      isTimeEOD: isReadyTimeEOD
    } = processTransitionTime(dateTime, calculationReady);
    readyDateTime = readyDate;
    readyDateTimeUI = isReadyTimeEOD ? format(readyDateTime, dateFormat, dateFormatOptions) + ' EOD' : format(readyDateTime, dateTimeFormat, dateFormatOptions);
  }

  const { discardDateTime, discardDateTimeUI } = getDiscardDateTime({
    calculationDiscard,
    dateDiscard,
    readyDateTime,
    locale,
    brand
  });

  const day = format(discardDateTime, 'eee', dateFormatOptions).toUpperCase();
  const mrdDateHeadingProps = {
    fontFamily: 'sans-serif',
    fontSize: 20,
    fill: "#000000",
  };
  const mrdDateProps = {
    fontFamily: 'sans-serif',
    fontSize: 20,
    fontWeight: 400,
    fill: "#000000",
  };
  const mrdDayProps = {
    fontFamily: 'sans-serif',
    fontSize: 56,
    fontWeight: '600',
    fill: "#000000",
  };

  const mrdDiscardDayProps = {
    fontFamily: 'sans-serif',
    fontSize: 26,
    fontWeight: '600',
    fill: "#000000",
  };

  const madeDate = format(dateTime, dateTimeFormat, dateFormatOptions);

  return (
    <svg direction="ltr" width="384px" height="192px" viewBox="0 0 384 192" version="1.1"
      xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
      <title>Label</title>
      <g id="Label" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect fill="#ffffff" x="0" y="0" width="384" height="192"></rect>
        <LabelHeadingSVG productHeading={productHeading} productSubheading={productSubheading} pic={pic} picText={picText}/>
        <text x="10" y="92" {...mrdDateHeadingProps}>{madeText}</text>
        <text x="10" y="116" {...mrdDateProps}>{madeDate}</text>
        <text x="202" y="92" {...mrdDateHeadingProps}>{readyText}</text>
        <text x="202" y="116" {...mrdDateProps}>{readyDateTimeUI}</text>
        <text x="10" y="145" {...mrdDateHeadingProps} fontWeight={"bold"} fontSize={20}>{discardText}</text>
        <text x="10" y="176" {...mrdDiscardDayProps} >{discardDateTimeUI}</text>
        <rect fill="#000000" x="192" y="70" width="2" height="54"></rect>
        <rect fill="#000000" x="0" y="70" height="2" width="376"></rect>
        <rect fill="#000000" x="0" y="122" height="2" width="376"></rect>
        <text x="230" y="176"
              {...mrdDayProps}
        >
          {day}
        </text>
      </g>
    </svg>
  )
}

export function getImageDataFromSVG(svgHtml: string, renderedCallback: any) {
  const uri = 'data:image/svg+xml;base64,' + window.btoa(unescape(encodeURIComponent(svgHtml)));
  const image = new Image();

  image.onload = function () {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context: any = canvas.getContext('2d');
    context.drawImage(image, 0, 0);
    const data = context.getImageData(0, 0, image.width, image.height);
    renderedCallback(data);
  }
  image.src = uri;

  return image;
}

interface IGetDiscardDateTimeParams {
  calculationDiscard: string;
  dateDiscard: Date | null | undefined;
  readyDateTime: Date;
  locale: string;
  brand: any;
}

export const getDiscardDateTime = ({
  calculationDiscard,
  dateDiscard,
  readyDateTime,
  locale,
  brand
}: IGetDiscardDateTimeParams) => {
  const dateFormat = brand.id.includes('US') ? DATE_FORMAT_US : DATE_FORMAT;
  const dateTimeFormat = brand.id.includes('US') ? DATE_TIME_FORMAT_US : DATE_TIME_FORMAT;
  const dateFormatOptions = { locale: DATE_LOCALES[locale] };
  
  let discardDateTime = readyDateTime;
  let discardDateTimeUI = format(discardDateTime, dateTimeFormat, dateFormatOptions);

  if (isUBD(calculationDiscard)) {
    if (!dateDiscard) {
      discardDateTimeUI = '';
    } else {
      discardDateTime = dateDiscard;
      discardDateTimeUI = format(dateDiscard, dateFormat, dateFormatOptions);
    }
  } else if (isEOD(calculationDiscard)) {
    discardDateTime = endOfDay(discardDateTime)
    discardDateTimeUI = calculationDiscard;
  } else if (calculationDiscard) {
    const {
      date: discardDate,
      isTimeEOD: isDiscardTimeEOD
    } = processTransitionTime(readyDateTime, calculationDiscard);
    discardDateTime = discardDate;
    discardDateTimeUI = isDiscardTimeEOD 
      ? format(discardDateTime, dateFormat, dateFormatOptions) + ' EOD' 
      : format(discardDateTime, dateTimeFormat, dateFormatOptions);
  }

  return {
    discardDateTime,
    discardDateTimeUI
  };
};

export const validateCalculationField = (value: string) => {
  const isDays = daysRegExp.test(value)
  const isHours = hoursRegExp.test(value)
  const isMins = minsRegExp.test(value)
  return isDays || isHours || isMins || isEOD(value) || isEqual(value, '') || isUBD(value);
};
